/**
 * space/pointer.js — look-and-select interaction for the personal space.
 *
 * The space has no throwables, so the hands are free to point instead. One
 * raycast per frame drives everything: hovering a fish raises its name label,
 * clicking a plush lifts it, clicking a slot puts it down, clicking a plaque
 * opens a menu.
 *
 * The ray comes from the same place the player is looking in both modes:
 *   - VR      → the right controller's forward axis
 *   - desktop → the camera, through the crosshair (or the raw mouse position
 *               when the pointer is not locked, which is the case in tests)
 *
 * Nothing here knows what a fish or a shelf is. Callers register an Object3D
 * with `onSelect`/`onHover` and get called back.
 *
 * @module space/pointer
 */

import * as THREE from 'three';
import { input } from '../core/input.js';
import { createPanel } from '../ui/panels.js';

/** An object3D is only pickable when it and every ancestor is visible. */
function isVisible(obj) {
  for (let o = obj; o; o = o.parent) if (o.visible === false) return false;
  return true;
}

class SpacePointer {
  constructor() {
    /** @type {THREE.Camera} */ this.camera = null;
    this.raycaster = new THREE.Raycaster();
    this.raycaster.far = 14;
    this.enabled = false;

    /** @type {Map<string, {object3D: THREE.Object3D, id: string, group: string,
     *   onSelect?: Function, onHover?: Function, label?: string, meshes: THREE.Object3D[]}>} */
    this.items = new Map();
    /** @type {object|null} */ this.hovered = null;

    this._ndc = new THREE.Vector2(0, 0);
    this._pointerNdc = new THREE.Vector2(0, 0);
    this._havePointer = false;
    this._origin = new THREE.Vector3();
    this._dir = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    this._hits = [];
    this._bound = false;
  }

  /**
   * @param {THREE.Camera} camera
   * @param {HTMLCanvasElement} [canvas]
   */
  init(camera, canvas) {
    this.camera = camera;
    if (canvas && !this._bound) {
      this._bound = true;
      canvas.addEventListener('mousemove', (e) => {
        const r = canvas.getBoundingClientRect();
        this._pointerNdc.set(
          ((e.clientX - r.left) / r.width) * 2 - 1,
          -((e.clientY - r.top) / r.height) * 2 + 1,
        );
        this._havePointer = true;
      });
    }
    return this;
  }

  /**
   * Make an object pickable.
   * @param {THREE.Object3D} object3D
   * @param {{ id: string, group?: string, label?: string,
   *           onSelect?: (rec: object) => void,
   *           onHover?: (on: boolean, rec: object) => void }} opts
   */
  register(object3D, opts) {
    const meshes = [];
    object3D.traverse((o) => { if (o.isMesh || o.isSprite) meshes.push(o); });
    const rec = {
      object3D, meshes,
      id: opts.id,
      group: opts.group || 'default',
      label: opts.label || '',
      onSelect: opts.onSelect,
      onHover: opts.onHover,
    };
    this.items.set(opts.id, rec);
    return rec;
  }

  /** @param {string} id */
  unregister(id) {
    if (this.hovered && this.hovered.id === id) this._setHover(null);
    this.items.delete(id);
  }

  /** Drop every registration (called when the room is rebuilt). */
  clear() {
    this._setHover(null);
    this.items.clear();
  }

  /** @param {string} group @returns {object[]} */
  byGroup(group) {
    return [...this.items.values()].filter((r) => r.group === group);
  }

  /**
   * Fire an item's select handler without any input — the path `__cc` uses.
   * @param {string} id
   * @returns {boolean} False if the id is not registered.
   */
  selectById(id) {
    const rec = this.items.get(id);
    if (!rec || !rec.onSelect) return false;
    rec.onSelect(rec);
    return true;
  }

  /** @returns {string|null} The id currently under the pointer. */
  hoveredId() {
    return this.hovered ? this.hovered.id : null;
  }

  update() {
    if (!this.enabled || !this.camera || this.items.size === 0) {
      if (this.hovered) this._setHover(null);
      return;
    }

    this._aim();
    let best = null;
    let bestDist = Infinity;

    for (const rec of this.items.values()) {
      if (!isVisible(rec.object3D)) continue;
      for (const m of rec.meshes) {
        if (!isVisible(m)) continue;
        this._hits.length = 0;
        this.raycaster.intersectObject(m, false, this._hits);
        if (this._hits.length && this._hits[0].distance < bestDist) {
          bestDist = this._hits[0].distance;
          best = rec;
        }
      }
    }

    if (best !== this.hovered) this._setHover(best);
    if (best && this._selectPressed()) best.onSelect?.(best);
  }

  /** @private */
  _aim() {
    if (input.isVR) {
      const hand = input.getHand('right');
      this._origin.copy(hand.position);
      this._dir.copy(hand.direction).normalize();
      this.raycaster.set(this._origin, this._dir);
      return;
    }
    // Crosshair when the pointer is locked (the cursor is parked dead centre),
    // the real cursor otherwise — which is what a headless test drives.
    const ndc = (input.pointerLocked || !this._havePointer) ? this._ndc : this._pointerNdc;
    this.raycaster.setFromCamera(ndc, this.camera);
  }

  /** @private */
  _selectPressed() {
    if (input.isVR) {
      return input.getHand('right').triggerJustPressed || input.getHand('left').triggerJustPressed;
    }
    return input.getActiveHand().gripJustPressed || input.wasPressed('activate');
  }

  /** @private */
  _setHover(rec) {
    if (this.hovered) this.hovered.onHover?.(false, this.hovered);
    this.hovered = rec;
    if (rec) rec.onHover?.(true, rec);
  }
}

/** The one pointer for the personal space. */
export const pointer = new SpacePointer();

// ─────────────────────────────────────────────────────────────────────────────
// Menu panels — the in-world UI every purchase and choice flows through.
// Built here because the buttons are pointer items and nothing else needs them.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} MenuItem
 * @property {string} id
 * @property {string} label
 * @property {string} [sub] Right-hand column, e.g. a star price.
 * @property {boolean} [disabled]
 */

/**
 * A floating menu: a titled backing panel with one clickable plane per item.
 *
 * @param {{ title: string, items: MenuItem[], width?: number,
 *           onPick: (id: string) => void, idPrefix: string }} opts
 * @returns {THREE.Group} With `userData.dispose()` and `userData.itemIds`.
 */
export function createMenu(opts) {
  const width = opts.width ?? 1.5;
  const rowH = 0.19;
  const items = opts.items.slice(0, 8);
  const height = 0.3 + items.length * rowH;

  const g = new THREE.Group();
  g.name = `space-menu-${opts.idPrefix}`;

  const back = createPanel({
    title: opts.title, width, height, lines: [], align: 'center',
    bg: '#151b30', accent: 'rgba(255,209,102,0.75)',
  });
  g.add(back);

  const ids = [];
  const top = height / 2 - 0.3;
  items.forEach((item, i) => {
    const id = `${opts.idPrefix}:${item.id}`;
    ids.push(id);
    const row = createPanel({
      width: width - 0.14, height: rowH - 0.03,
      lines: [item.sub ? `${item.label}   ${item.sub}` : item.label],
      bg: item.disabled ? '#232838' : '#26304f',
      accent: item.disabled ? 'rgba(120,130,150,0.4)' : 'rgba(111,231,196,0.6)',
      textColor: item.disabled ? '#7d879c' : '#e8eefc',
      radius: 14,
    });
    row.position.set(0, top - i * rowH, 0.004);
    g.add(row);

    pointer.register(row, {
      id,
      group: `menu:${opts.idPrefix}`,
      label: item.label,
      onHover: (on) => {
        row.setStyle({ bg: on && !item.disabled ? '#37456f' : (item.disabled ? '#232838' : '#26304f') });
      },
      onSelect: () => { if (!item.disabled) opts.onPick(item.id); },
    });
  });

  g.userData.itemIds = ids;
  g.userData.dispose = () => {
    for (const id of ids) pointer.unregister(id);
    g.traverse((o) => { if (o.dispose) o.dispose(); });
  };
  return g;
}

/**
 * Only one menu may be open at a time — two overlapping in-world panels are
 * unreadable, and in VR they fight for the same pointer ray.
 */
class MenuHost {
  constructor() {
    /** @type {THREE.Group|null} */ this.current = null;
    this.currentKey = null;
  }

  /**
   * Open a menu, replacing whatever was open. Re-opening the same key closes it,
   * so a plaque toggles rather than stacking.
   * @param {{ key: string, parent: THREE.Object3D, position: THREE.Vector3|{x,y,z},
   *           rotationY?: number }} where
   * @param {{ title: string, items: MenuItem[], width?: number,
   *           onPick: (id: string) => void, idPrefix: string }} spec
   * @returns {THREE.Group|null} Null when the call closed an already-open menu.
   */
  open(where, spec) {
    const wasKey = this.currentKey;
    this.close();
    if (wasKey === where.key) return null;

    const menu = createMenu(spec);
    menu.position.set(where.position.x, where.position.y, where.position.z);
    menu.rotation.y = where.rotationY ?? 0;
    where.parent.add(menu);
    this.current = menu;
    this.currentKey = where.key;
    return menu;
  }

  close() {
    if (!this.current) return;
    this.current.userData.dispose?.();
    this.current.parent?.remove(this.current);
    this.current = null;
    this.currentKey = null;
  }

  /** @returns {string|null} */
  openKey() {
    return this.currentKey;
  }
}

/** The one open menu. */
export const menuHost = new MenuHost();

/**
 * A small always-facing-camera text label.
 * @param {string} text
 * @param {{ color?: string, scale?: number, bg?: string }} [opts]
 * @returns {THREE.Sprite} With `userData.setText(t)`.
 */
export function createLabel(text, opts = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  const draw = (t) => {
    ctx.clearRect(0, 0, 512, 128);
    ctx.fillStyle = opts.bg ?? 'rgba(16,20,36,0.82)';
    const w = Math.min(500, ctx.measureText(String(t)).width + 300);
    roundRect(ctx, { x: (512 - w) / 2, y: 18, w, h: 92, r: 26 });
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,209,102,0.7)';
    ctx.lineWidth = 4;
    roundRect(ctx, { x: (512 - w) / 2 + 2, y: 20, w: w - 4, h: 88, r: 24 });
    ctx.stroke();
    ctx.font = 'bold 54px "Trebuchet MS", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = opts.color ?? '#ffd166';
    ctx.fillText(String(t), 256, 64);
  };
  ctx.font = 'bold 54px "Trebuchet MS", sans-serif';
  draw(text);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(material);
  const s = opts.scale ?? 0.42;
  sprite.scale.set(s * 4, s, 1);
  sprite.renderOrder = 8000;
  sprite.userData.setText = (t) => { draw(t); texture.needsUpdate = true; };
  sprite.userData.dispose = () => { texture.dispose(); material.dispose(); };
  return sprite;
}

function roundRect(ctx, rect) {
  const { x, y, w, h, r } = rect;
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
